import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Calendar, FileText, Loader2, Shield,
  ClipboardList, Brain, Activity, Heart, Users, Eye, Pill, AlertTriangle, Stethoscope, Clock,
  Pencil, Trash2, Sparkles, TrendingUp, TrendingDown, Minus, ChevronUp, ChevronDown, RefreshCw, Target, Flag, Save
} from 'lucide-react';

const TEMPLATE_ICONS = {
  intake: ClipboardList,
  cbt: Brain,
  dbt: Activity,
  grief: Heart,
  couples: Users,
  emdr: Eye,
  med_management: Pill,
  crisis: AlertTriangle
};

const TEMPLATE_COLORS = {
  purple: 'border-purple-300 bg-purple-50 hover:border-purple-500 hover:bg-purple-100',
  blue: 'border-blue-300 bg-blue-50 hover:border-blue-500 hover:bg-blue-100',
  teal: 'border-teal-300 bg-teal-50 hover:border-teal-500 hover:bg-teal-100',
  indigo: 'border-indigo-300 bg-indigo-50 hover:border-indigo-500 hover:bg-indigo-100',
  rose: 'border-rose-300 bg-rose-50 hover:border-rose-500 hover:bg-rose-100',
  amber: 'border-amber-300 bg-amber-50 hover:border-amber-500 hover:bg-amber-100',
  green: 'border-green-300 bg-green-50 hover:border-green-500 hover:bg-green-100',
  red: 'border-red-300 bg-red-50 hover:border-red-500 hover:bg-red-100'
};

const TEMPLATE_ICON_COLORS = {
  purple: 'text-purple-600',
  blue: 'text-blue-600',
  teal: 'text-teal-600',
  indigo: 'text-indigo-600',
  rose: 'text-rose-600',
  amber: 'text-amber-600',
  green: 'text-green-600',
  red: 'text-red-600'
};

const SELECTED_RING = {
  purple: 'ring-2 ring-purple-500 border-purple-500 bg-purple-100',
  blue: 'ring-2 ring-blue-500 border-blue-500 bg-blue-100',
  teal: 'ring-2 ring-teal-500 border-teal-500 bg-teal-100',
  indigo: 'ring-2 ring-indigo-500 border-indigo-500 bg-indigo-100',
  rose: 'ring-2 ring-rose-500 border-rose-500 bg-rose-100',
  amber: 'ring-2 ring-amber-500 border-amber-500 bg-amber-100',
  green: 'ring-2 ring-green-500 border-green-500 bg-green-100',
  red: 'ring-2 ring-red-500 border-red-500 bg-red-100'
};

const SESSION_BADGE_COLORS = {
  purple: 'bg-purple-100 text-purple-700',
  blue: 'bg-blue-100 text-blue-700',
  teal: 'bg-teal-100 text-teal-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  rose: 'bg-rose-100 text-rose-700',
  amber: 'bg-amber-100 text-amber-700',
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700'
};

// Return the short timezone label (e.g. "EDT", "PST") for an IANA name.
// Uses the browser's Intl implementation — works for every valid zone.
function tzShortLabel(tz) {
  if (!tz) return '';
  try {
    const s = new Date().toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' });
    const parts = s.split(' ');
    const label = parts[parts.length - 1];
    if (label && !label.startsWith('AM') && !label.startsWith('PM')) return label;
    return tz;
  } catch {
    return tz;
  }
}

// Defensively coerce any field that our render code calls `.map()` on into
// an array. LLMs occasionally emit a single string or null for array keys.
function _toArray(v) {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  if (typeof v === 'string') {
    // Split on bullets / newlines / semicolons so a stringified list still renders.
    return v.split(/\s*(?:\n|•|\u2022|;|\u2013|\u2014|- )\s*/).map(s => s.trim()).filter(Boolean);
  }
  return [v];
}

function normalizeBriefing(resp) {
  if (!resp || !resp.eligible || !resp.briefing) return resp;
  const b = resp.briefing || {};
  const normalized = {
    ...b,
    active_diagnoses: _toArray(b.active_diagnoses),
    key_themes: _toArray(b.key_themes),
    symptom_trend: _toArray(b.symptom_trend).map(item => {
      if (item && typeof item === 'object') {
        return {
          symptom: item.symptom || item.name || String(item),
          direction: (item.direction || '').toString(),
          notes: item.notes || item.note || '',
        };
      }
      return { symptom: String(item), direction: '', notes: '' };
    }),
    what_has_worked: _toArray(b.what_has_worked),
    what_has_not_worked: _toArray(b.what_has_not_worked),
    outstanding_homework: _toArray(b.outstanding_homework),
    focus_for_today: _toArray(b.focus_for_today),
    watch_for: _toArray(b.watch_for),
    med_changes: _toArray(b.med_changes),
  };
  return { ...resp, briefing: normalized };
}


export default function PatientDetail({ user, token, onLogout }) {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  // Session mode (in_person | telehealth). Default to the user's last
  // choice so a telehealth-heavy provider isn't forced to pick every time.
  const [sessionMode, setSessionMode] = useState(() => {
    try {
      const saved = localStorage.getItem('pura_last_session_mode');
      return saved === 'telehealth' ? 'telehealth' : 'in_person';
    } catch { return 'in_person'; }
  });

  // ── Edit / Delete session state ──────────────────────────
  const [editingSession, setEditingSession] = useState(null); // full session obj when editing
  const [editDraft, setEditDraft] = useState({ session_type: '', session_date: '', start_time: '', end_time: '', notes: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingSession, setDeletingSession] = useState(null); // session obj when confirming delete
  const [deleting, setDeleting] = useState(false);

  // ── Pre-session AI progress briefing ─────────────────────
  const [briefing, setBriefing] = useState(null); // { eligible, session_count, briefing: {...} }
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingExpanded, setBriefingExpanded] = useState(true);
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);
  const [briefingAcknowledged, setBriefingAcknowledged] = useState(false);
  const loadBriefing = async (force = false) => {
    if (briefingLoading) return;
    if (briefing && !force) return;
    setBriefingLoading(true);
    try {
      const res = await axios.get(`${API}/patients/${patientId}/progress-briefing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBriefing(normalizeBriefing(res.data));
    } catch {
      // Silent — briefing is non-critical; user can retry via refresh button
    } finally {
      setBriefingLoading(false);
    }
  };

  // ── Edit / Delete patient
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', date_of_birth: '', mrn: '', consent_granted: false });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingPatient, setDeletingPatient] = useState(false);

  const openEditPatientModal = () => {
    if (!patient) return;
    setEditForm({
      first_name: patient.first_name || '',
      last_name: patient.last_name || '',
      date_of_birth: patient.date_of_birth || '',
      mrn: patient.mrn || '',
      consent_granted: !!patient.consent_granted,
    });
    setEditModalOpen(true);
  };
  const saveEditedPatient = async () => {
    if (!editForm.first_name.trim() || !editForm.last_name.trim() || !editForm.date_of_birth) {
      toast.error('First name, last name, and date of birth are required');
      return;
    }
    setEditSaving(true);
    try {
      const res = await axios.put(`${API}/patients/${patientId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatient(res.data);
      toast.success('Patient updated');
      setEditModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update patient');
    } finally {
      setEditSaving(false);
    }
  };
  const deletePatientNow = async () => {
    setDeletingPatient(true);
    try {
      const res = await axios.delete(`${API}/patients/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.soft_deleted) {
        toast.success('Moved to Trash — restore within 24h from your user menu', { duration: 6000 });
      } else {
        toast.success(`Deleted patient and ${res.data?.sessions_deleted ?? 0} session(s)`);
      }
      // Return to the previous page (dashboard, session list, etc.) rather
      // than forcing the user back to a fixed dashboard route. Falls back to
      // dashboard if there's no history entry to return to.
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete patient');
    } finally {
      setDeletingPatient(false);
      setDeleteModalOpen(false);
    }
  };

  // "New Session" click handler — for repeat patients (eligible briefing),
  // surface the briefing modal FIRST; once acknowledged, open the session
  // type picker. For first-time patients, skip straight to the picker.
  const handleNewSessionClick = () => {
    if (briefing?.eligible && briefing?.briefing && !briefingAcknowledged) {
      setBriefingModalOpen(true);
    } else if (briefingLoading && briefing === null) {
      // Briefing still loading — open the modal so the clinician sees progress
      setBriefingModalOpen(true);
    } else {
      setDialogOpen(true);
    }
  };
  const acknowledgeBriefingAndContinue = () => {
    setBriefingAcknowledged(true);
    setBriefingModalOpen(false);
    setDialogOpen(true);
  };

  const openEditSession = (session, e) => {
    if (e) e.stopPropagation();
    setEditingSession(session);
    setEditDraft({
      session_type: session.session_type || '',
      session_date: session.session_date || '',
      start_time: session.start_time || '',
      end_time: session.end_time || '',
      notes: session.notes || ''
    });
  };

  const saveSessionEdit = async () => {
    if (!editingSession) return;
    setSavingEdit(true);
    try {
      // Only send fields the user actually changed (keeps audit trail clean)
      const payload = {};
      const orig = editingSession;
      if (editDraft.session_type !== (orig.session_type || '')) payload.session_type = editDraft.session_type;
      if (editDraft.session_date !== (orig.session_date || '')) payload.session_date = editDraft.session_date || null;
      if (editDraft.start_time !== (orig.start_time || '')) payload.start_time = editDraft.start_time || null;
      if (editDraft.end_time !== (orig.end_time || '')) payload.end_time = editDraft.end_time || null;
      if (editDraft.notes !== (orig.notes || '')) payload.notes = editDraft.notes;
      if (Object.keys(payload).length === 0) {
        toast.info('No changes to save');
        setEditingSession(null);
        return;
      }
      const res = await axios.patch(
        `${API}/sessions/${editingSession.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSessions(prev => prev.map(s => s.id === editingSession.id ? { ...s, ...res.data } : s));
      toast.success('Session updated');
      setEditingSession(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update session');
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteSession = (session, e) => {
    if (e) e.stopPropagation();
    setDeletingSession(session);
  };

  const confirmDeleteSession = async () => {
    if (!deletingSession) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/sessions/${deletingSession.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(prev => prev.filter(s => s.id !== deletingSession.id));
      toast.success('Session and all its notes deleted');
      setDeletingSession(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete session');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTemplates();
  }, [patientId]);

  const fetchData = async () => {
    try {
      const [patientRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/patients/${patientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/sessions/patient/${patientId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setPatient(patientRes.data);
      setSessions(sessionsRes.data);
      // Auto-fetch the pre-session briefing for repeat patients (≥2 completed sessions).
      const completedCount = sessionsRes.data.filter(s => s.transcription_status === 'completed').length;
      if (completedCount >= 2) {
        loadBriefing();
      }
    } catch (error) {
      toast.error('Failed to load patient data');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/session-templates`);
      setTemplates(res.data);
    } catch {
      // Templates are optional
    }
  };

  const handleCreateSession = async () => {
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one session type');
      return;
    }
    setCreating(true);
    try {
      const hasIntake = selectedTemplates.includes('intake');
      const hasCrisis = selectedTemplates.includes('crisis');
      const sessionType = hasIntake ? 'initial' : hasCrisis ? 'emergency' : 'follow-up';
      try { localStorage.setItem('pura_last_session_mode', sessionMode); } catch {}
      const response = await axios.post(
        `${API}/sessions`,
        {
          patient_id: patientId,
          session_type: sessionType,
          template_ids: selectedTemplates,
          notes: notes || undefined,
          session_mode: sessionMode,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Session created');
      setDialogOpen(false);
      setSelectedTemplates([]);
      setNotes('');
      navigate(`/session/${response.data.id}/record`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'processing': return 'bg-blue-100 text-blue-700';
      case 'recording': return 'bg-purple-100 text-purple-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTemplateBadges = (session) => {
    const ids = session.template_ids || (session.template_id ? [session.template_id] : []);
    if (ids.length === 0) return null;
    return ids.map(tid => {
      const tpl = templates.find(t => t.id === tid);
      if (!tpl) return null;
      const Icon = TEMPLATE_ICONS[tpl.id] || Stethoscope;
      const badgeColor = SESSION_BADGE_COLORS[tpl.color] || 'bg-gray-100 text-gray-700';
      return (
        <span key={tid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
          <Icon className="w-3 h-3" />
          {tpl.name}
        </span>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #f8f4ff 50%, #e8eaf6 100%)' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-white/50 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                data-testid="back-button"
                className="rounded-xl border-gray-200 hover:bg-gray-50 shrink-0 px-3 sm:px-4"
              >
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {patient?.first_name} {patient?.last_name}
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">DOB: {patient?.date_of_birth}</p>
              </div>
            </div>
            {patient?.consent_granted && (
              <div className="flex items-center gap-1.5 sm:gap-2 bg-green-100 text-green-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium shrink-0">
                <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Consent Granted</span>
                <span className="sm:hidden">Consent</span>
              </div>
            )}
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openEditPatientModal}
                data-testid="edit-patient-btn"
                className="rounded-xl border-gray-200 hover:bg-gray-50 text-gray-700"
                title="Edit patient"
              >
                <Pencil className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDeleteModalOpen(true)}
                data-testid="delete-patient-btn"
                className="rounded-xl border-red-200 hover:bg-red-50 text-red-700"
                title="Delete patient"
              >
                <Trash2 className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Delete</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Action Bar */}
        <div className="mb-8">
          {patient?.consent_granted ? (
            <>
              <Button
                data-testid="new-session-button"
                onClick={handleNewSessionClick}
                className="h-12 rounded-xl bg-gradient-to-r from-purple-600 to-slate-600 hover:from-purple-700 hover:to-slate-700 text-white font-medium shadow-lg hover:shadow-xl transition-all"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Session
              </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setSelectedTemplates([]); setNotes(''); } }}>
              <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-slate-700 bg-clip-text text-transparent">
                    Choose Session Type{selectedTemplates.length > 0 && <span className="text-sm font-normal text-gray-500 ml-2">({selectedTemplates.length} selected)</span>}
                  </DialogTitle>
                </DialogHeader>

                {/* Template Grid */}
                <div className="grid grid-cols-2 gap-3 mt-4" data-testid="template-grid">
                  {templates.map((tpl) => {
                    const Icon = TEMPLATE_ICONS[tpl.id] || Stethoscope;
                    const isSelected = selectedTemplates.includes(tpl.id);
                    const colorClass = isSelected
                      ? SELECTED_RING[tpl.color] || 'ring-2 ring-purple-500'
                      : TEMPLATE_COLORS[tpl.color] || 'border-gray-200 bg-gray-50';
                    const iconColor = TEMPLATE_ICON_COLORS[tpl.color] || 'text-gray-600';
                    return (
                      <button
                        key={tpl.id}
                        onClick={() => setSelectedTemplates(prev =>
                          prev.includes(tpl.id) ? prev.filter(id => id !== tpl.id) : [...prev, tpl.id]
                        )}
                        data-testid={`template-${tpl.id}`}
                        className={`p-4 rounded-2xl border-2 text-left transition-all ${colorClass}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 ${iconColor}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 text-sm">{tpl.name}</p>
                            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{tpl.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Selected Template Details */}
                {selectedTemplates.length > 0 && (() => {
                  const selectedTpls = templates.filter(t => selectedTemplates.includes(t.id));
                  // Merge and deduplicate codes
                  const seen = { cpt: new Set(), em: new Set(), addon: new Set(), icd: new Set() };
                  const allCpt = [], allEm = [], allAddon = [], allIcd = [];
                  for (const t of selectedTpls) {
                    for (const c of (t.default_cpt_codes || [])) {
                      if (!seen.cpt.has(c.code)) { seen.cpt.add(c.code); allCpt.push(c); }
                    }
                    for (const c of (t.em_codes || [])) {
                      if (!seen.em.has(c.code)) { seen.em.add(c.code); allEm.push(c); }
                    }
                    for (const c of (t.addon_codes || [])) {
                      if (!seen.addon.has(c.code)) { seen.addon.add(c.code); allAddon.push(c); }
                    }
                    for (const c of (t.default_icd10_codes || [])) {
                      if (!seen.icd.has(c.code)) { seen.icd.add(c.code); allIcd.push(c); }
                    }
                  }
                  const names = selectedTpls.map(t => t.name).join(' + ');
                  return (
                    <div className="mt-4 bg-gray-50 rounded-2xl p-4 space-y-3" data-testid="template-details">
                      <p className="text-sm font-semibold text-gray-700">{names} — Billing Codes</p>
                      <div className="flex flex-wrap gap-1.5">
                        {allCpt.map((c, i) => (
                          <span key={`cpt-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-100 text-blue-800 text-xs font-mono">
                            CPT {c.code} <span className="font-sans text-blue-600">— {c.description}</span>
                          </span>
                        ))}
                        {allEm.map((c, i) => (
                          <span key={`em-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-green-100 text-green-800 text-xs font-mono">
                            E/M {c.code} <span className="font-sans text-green-600">— {c.description}</span>
                          </span>
                        ))}
                        {allAddon.map((c, i) => (
                          <span key={`addon-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-mono">
                            Add-on {c.code} <span className="font-sans text-amber-600">— {c.description}</span>
                          </span>
                        ))}
                        {allIcd.map((c, i) => (
                          <span key={`icd-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-100 text-purple-800 text-xs font-mono">
                            ICD-10 {c.code} <span className="font-sans text-purple-600">— {c.description}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Notes */}
                <div className="mt-4">
                  <Label htmlFor="notes">Session Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Pre-session notes..."
                    data-testid="notes-textarea"
                    rows={3}
                    className="mt-1.5 rounded-xl"
                  />
                </div>

                {/* Session Mode — in-person vs telehealth. Drives the
                    recorder's audio pipeline (echo cancellation, tab
                    capture) so patient audio stays accurate on Zoom/Meet. */}
                <div className="mt-4" data-testid="session-mode-picker">
                  <Label>Session Mode</Label>
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setSessionMode('in_person')}
                      data-testid="session-mode-in-person"
                      aria-pressed={sessionMode === 'in_person'}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        sessionMode === 'in_person'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-semibold text-gray-900">In-person</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">Office session — single mic with echo cancellation.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSessionMode('telehealth')}
                      data-testid="session-mode-telehealth"
                      aria-pressed={sessionMode === 'telehealth'}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        sessionMode === 'telehealth'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-semibold text-gray-900">Telehealth</div>
                      <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">Zoom / Meet / Doxy — picks mic + tab audio for accurate patient capture.</div>
                    </button>
                  </div>
                </div>

                <Button
                  onClick={handleCreateSession}
                  disabled={selectedTemplates.length === 0 || creating}
                  data-testid="submit-session-button"
                  className="w-full h-11 mt-4 rounded-xl bg-gradient-to-r from-purple-600 to-slate-600 hover:from-purple-700 hover:to-slate-700 disabled:opacity-50"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create & Start Recording
                </Button>
              </DialogContent>
            </Dialog>
            </>
          ) : (
            <Card className="p-6 bg-amber-50 border-amber-200 rounded-2xl" data-testid="consent-warning">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">Consent Required</h3>
                  <p className="text-sm text-amber-800">Patient consent must be granted before creating sessions.</p>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* ──────── Pre-Session AI Clinical Progress Briefing ──────── */}
        {briefing?.eligible && briefing.briefing && (
          <Card data-testid="progress-briefing-card" className="mb-6 rounded-2xl border-0 overflow-hidden shadow-sm bg-gradient-to-br from-[#F9F6F0] via-white to-[#EFF4EE]">
            <div
              className="flex items-start justify-between gap-3 p-5 cursor-pointer select-none"
              onClick={() => setBriefingExpanded(v => !v)}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ring-1 ring-[#2C4A3B]/15"
                  style={{ background: 'linear-gradient(135deg, #2C4A3B 0%, #1E3329 100%)' }}>
                  <Sparkles className="w-5 h-5 text-[#F9F6F0]" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base font-semibold tracking-tight text-[#1E3329]">Pre-session clinical briefing</h2>
                    {(() => {
                      const t = (briefing.briefing.trajectory || '').toLowerCase();
                      const map = {
                        improving: { icon: TrendingUp, tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
                        stable:    { icon: Minus,      tone: 'bg-blue-50 text-blue-700 ring-blue-200' },
                        mixed:     { icon: Activity,   tone: 'bg-amber-50 text-amber-700 ring-amber-200' },
                        worsening: { icon: TrendingDown, tone: 'bg-red-50 text-red-700 ring-red-200' },
                        complex:   { icon: Activity,   tone: 'bg-purple-50 text-purple-700 ring-purple-200' },
                      }[t] || { icon: Activity, tone: 'bg-stone-100 text-stone-700 ring-stone-200' };
                      const Icon = map.icon;
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${map.tone}`}>
                          <Icon className="w-3 h-3" />
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </span>
                      );
                    })()}
                    <span className="text-[11px] text-stone-500">
                      {briefing.session_count} prior sessions · {briefing.date_from} → {briefing.date_to}
                    </span>
                  </div>
                  {briefing.briefing.headline && (
                    <p className="text-[13px] text-[#2C4A3B] mt-1 leading-relaxed">{briefing.briefing.headline}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); loadBriefing(true); }}
                  disabled={briefingLoading}
                  data-testid="progress-briefing-refresh-btn"
                  className="p-1.5 rounded-full hover:bg-[#E3E8E4] text-[#2C4A3B] transition-colors disabled:opacity-50"
                  title="Regenerate briefing"
                >
                  {briefingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>
                {briefingExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
              </div>
            </div>

            {briefingExpanded && (
              <div className="px-5 pb-5 pt-1 space-y-4">
                {/* Trajectory rationale */}
                {briefing.briefing.trajectory_rationale && (
                  <div className="text-[13px] text-[#1E3329] leading-relaxed bg-white/60 backdrop-blur rounded-lg p-3 ring-1 ring-[#E3E8E4]">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500 block mb-1">Trajectory</span>
                    {briefing.briefing.trajectory_rationale}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Active diagnoses */}
                  {briefing.briefing.active_diagnoses?.length > 0 && (
                    <div className="bg-white/60 rounded-lg p-3 ring-1 ring-[#E3E8E4]">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Stethoscope className="w-3.5 h-3.5 text-[#2C4A3B]" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Active diagnoses</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {briefing.briefing.active_diagnoses.map((d, i) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 bg-[#E3E8E4] text-[#1E3329] rounded-full">{d}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Risk status */}
                  {briefing.briefing.risk_status && (
                    <div className="bg-white/60 rounded-lg p-3 ring-1 ring-[#E3E8E4]">
                      <div className="flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Risk status</span>
                      </div>
                      <p className="text-[12px] text-[#1E3329] leading-relaxed">{briefing.briefing.risk_status}</p>
                    </div>
                  )}
                </div>

                {/* Symptom trend */}
                {briefing.briefing.symptom_trend?.length > 0 && (
                  <div className="bg-white/60 rounded-lg p-3 ring-1 ring-[#E3E8E4]">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Activity className="w-3.5 h-3.5 text-[#2C4A3B]" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Symptom trend</span>
                    </div>
                    <div className="space-y-1.5">
                      {briefing.briefing.symptom_trend.map((s, i) => {
                        const dir = (s.direction || '').toLowerCase();
                        const DirIcon = dir === 'improving' ? TrendingUp : dir === 'worsening' ? TrendingDown : Minus;
                        const dirColor = dir === 'improving' ? 'text-emerald-600' : dir === 'worsening' ? 'text-red-600' : 'text-stone-500';
                        return (
                          <div key={i} className="flex items-start gap-2 text-[12px]">
                            <DirIcon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${dirColor}`} />
                            <div className="min-w-0">
                              <span className="font-semibold text-[#1E3329]">{s.symptom}</span>
                              {s.notes && <span className="text-stone-600"> — {s.notes}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* What worked */}
                  {briefing.briefing.what_has_worked?.length > 0 && (
                    <div className="bg-white/60 rounded-lg p-3 ring-1 ring-[#E3E8E4]">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Target className="w-3.5 h-3.5 text-emerald-700" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">What has worked</span>
                      </div>
                      <ul className="space-y-1 text-[12px] text-[#1E3329]">
                        {briefing.briefing.what_has_worked.map((w, i) => (
                          <li key={i} className="flex gap-1.5"><span className="text-emerald-600">•</span><span>{w}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Outstanding homework */}
                  {briefing.briefing.outstanding_homework?.length > 0 && (
                    <div className="bg-white/60 rounded-lg p-3 ring-1 ring-[#E3E8E4]">
                      <div className="flex items-center gap-1.5 mb-2">
                        <ClipboardList className="w-3.5 h-3.5 text-[#C47A5E]" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Outstanding homework</span>
                      </div>
                      <ul className="space-y-1 text-[12px] text-[#1E3329]">
                        {briefing.briefing.outstanding_homework.map((h, i) => (
                          <li key={i} className="flex gap-1.5"><span className="text-[#C47A5E]">•</span><span>{h}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Focus for today */}
                {briefing.briefing.focus_for_today?.length > 0 && (
                  <div className="rounded-lg p-3 ring-1 ring-[#2C4A3B]/20 bg-[#2C4A3B] text-[#F9F6F0]">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Flag className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-bold uppercase tracking-wider opacity-90">Recommended agenda for today</span>
                    </div>
                    <ul className="space-y-1 text-[12px]">
                      {briefing.briefing.focus_for_today.map((f, i) => (
                        <li key={i} className="flex gap-1.5"><span className="opacity-70">{i + 1}.</span><span>{f}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Watch-for red flags */}
                {briefing.briefing.watch_for?.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-3 ring-1 ring-red-200">
                    <div className="flex items-center gap-1.5 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-700" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-red-800">Watch for during this session</span>
                    </div>
                    <ul className="space-y-1 text-[12px] text-red-900">
                      {briefing.briefing.watch_for.map((w, i) => (
                        <li key={i} className="flex gap-1.5"><span>•</span><span>{w}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Key themes */}
                {briefing.briefing.key_themes?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500 self-center mr-1">Key themes:</span>
                    {briefing.briefing.key_themes.map((t, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 bg-[#E3E8E4] text-[#1E3329] rounded-full">{t}</span>
                    ))}
                  </div>
                )}

                <p className="text-[10px] text-stone-400 pt-2 border-t border-stone-200 mt-3">
                  AI-generated overview · Review with clinical judgment · Based on {briefing.session_count} prior sessions
                </p>
              </div>
            )}
          </Card>
        )}

        {/* ──────── Pre-Session Briefing Modal (triggered by "New Session") ──────── */}
        <Dialog open={briefingModalOpen} onOpenChange={setBriefingModalOpen}>
          <DialogContent
            className="sm:max-w-2xl rounded-3xl max-h-[88vh] overflow-y-auto"
            data-testid="briefing-modal"
          >
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1E3329] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#2C4A3B]" />
                Pre-session briefing · {patient?.first_name} {patient?.last_name}
              </DialogTitle>
            </DialogHeader>

            {briefingLoading && !briefing && (
              <div className="py-16 flex flex-col items-center justify-center gap-3 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#2C4A3B]" />
                <p className="text-sm text-stone-600">Assembling your clinical prep — usually under 2 seconds…</p>
              </div>
            )}

            {briefing?.eligible && briefing.briefing && (
              <div className="space-y-3 mt-2">
                {/* Trajectory badge + headline */}
                <div className="flex items-start gap-3">
                  {(() => {
                    const t = (briefing.briefing.trajectory || '').toLowerCase();
                    const map = {
                      improving: { icon: TrendingUp, tone: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
                      stable:    { icon: Minus,      tone: 'bg-blue-50 text-blue-700 ring-blue-200' },
                      mixed:     { icon: Activity,   tone: 'bg-amber-50 text-amber-700 ring-amber-200' },
                      worsening: { icon: TrendingDown, tone: 'bg-red-50 text-red-700 ring-red-200' },
                      complex:   { icon: Activity,   tone: 'bg-purple-50 text-purple-700 ring-purple-200' },
                    }[t] || { icon: Activity, tone: 'bg-stone-100 text-stone-700 ring-stone-200' };
                    const Icon = map.icon;
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ring-1 ${map.tone} shrink-0`}>
                        <Icon className="w-3.5 h-3.5" />
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </span>
                    );
                  })()}
                  <p className="text-[14px] text-[#1E3329] leading-relaxed font-medium">
                    {briefing.briefing.headline}
                  </p>
                </div>

                {briefing.briefing.trajectory_rationale && (
                  <p className="text-[12px] text-stone-600 leading-relaxed bg-[#F9F6F0] rounded-lg p-3 ring-1 ring-[#E3E8E4]">
                    {briefing.briefing.trajectory_rationale}
                  </p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {briefing.briefing.active_diagnoses?.length > 0 && (
                    <div className="rounded-lg p-3 ring-1 ring-[#E3E8E4] bg-white">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Stethoscope className="w-3.5 h-3.5 text-[#2C4A3B]" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Active diagnoses</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {briefing.briefing.active_diagnoses.map((d, i) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 bg-[#E3E8E4] text-[#1E3329] rounded-full">{d}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {briefing.briefing.risk_status && (
                    <div className="rounded-lg p-3 ring-1 ring-[#E3E8E4] bg-white">
                      <div className="flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Risk status</span>
                      </div>
                      <p className="text-[12px] text-[#1E3329] leading-relaxed">{briefing.briefing.risk_status}</p>
                    </div>
                  )}
                </div>

                {briefing.briefing.outstanding_homework?.length > 0 && (
                  <div className="rounded-lg p-3 ring-1 ring-[#E3E8E4] bg-white">
                    <div className="flex items-center gap-1.5 mb-2">
                      <ClipboardList className="w-3.5 h-3.5 text-[#C47A5E]" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Outstanding homework from last session</span>
                    </div>
                    <ul className="space-y-1 text-[12px] text-[#1E3329]">
                      {briefing.briefing.outstanding_homework.map((h, i) => (
                        <li key={i} className="flex gap-1.5"><span className="text-[#C47A5E]">•</span><span>{h}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {briefing.briefing.focus_for_today?.length > 0 && (
                  <div className="rounded-lg p-3 ring-1 ring-[#2C4A3B]/25 bg-[#2C4A3B] text-[#F9F6F0]">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Flag className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-bold uppercase tracking-wider opacity-90">Recommended agenda for today</span>
                    </div>
                    <ul className="space-y-1 text-[12px]">
                      {briefing.briefing.focus_for_today.map((f, i) => (
                        <li key={i} className="flex gap-1.5"><span className="opacity-70">{i + 1}.</span><span>{f}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                {briefing.briefing.watch_for?.length > 0 && (
                  <div className="rounded-lg p-3 ring-1 ring-red-200 bg-red-50">
                    <div className="flex items-center gap-1.5 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-700" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-red-800">Watch for during this session</span>
                    </div>
                    <ul className="space-y-1 text-[12px] text-red-900">
                      {briefing.briefing.watch_for.map((w, i) => (
                        <li key={i} className="flex gap-1.5"><span>•</span><span>{w}</span></li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-[10px] text-stone-400 pt-1">
                  AI-generated · {briefing.cached ? 'cached from last generation' : 'freshly generated'} · Review with clinical judgment
                </p>
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-3 mt-2 border-t border-stone-200">
              <Button
                variant="outline"
                onClick={() => setBriefingModalOpen(false)}
                className="rounded-xl"
                data-testid="briefing-modal-cancel-btn"
              >
                Close
              </Button>
              <Button
                onClick={acknowledgeBriefingAndContinue}
                disabled={briefingLoading || !briefing?.briefing}
                data-testid="briefing-modal-continue-btn"
                className="rounded-xl bg-gradient-to-r from-[#2C4A3B] to-[#1E3329] text-white hover:from-[#1E3329] hover:to-[#0F1A14]"
              >
                OK — continue to session types
                <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Sessions List */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Session History</h2>
          {sessions.length === 0 ? (
            <Card className="p-12 text-center bg-white/60 backdrop-blur rounded-2xl border-white/50" data-testid="no-sessions-message">
              <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No sessions yet</h3>
              <p className="text-gray-600">Create the first session to start recording</p>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="sessions-list">
              {sessions.map((session) => (
                <Card
                  key={session.id}
                  data-testid={`session-card-${session.id}`}
                  className="group relative p-4 sm:p-6 bg-transparent border-0 shadow-none rounded-2xl hover:bg-white/60 hover:shadow-[0_8px_30px_-12px_rgba(28,50,40,0.18)] hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                  onClick={() => {
                    if (session.transcription_status === 'completed') {
                      navigate(`/session/${session.id}/transcript`);
                    } else {
                      navigate(`/session/${session.id}/record`);
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-gradient-to-br from-purple-100 to-slate-100 p-2 sm:p-2.5 rounded-xl group-hover:from-purple-200 group-hover:to-slate-200 transition-colors shrink-0">
                          <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-gray-900 capitalize text-sm sm:text-base">{session.session_type} Session</h3>
                            {getTemplateBadges(session)}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
                            {session.session_date || new Date(session.created_at).toLocaleDateString()}
                            {session.start_time && (
                              <span className="ml-1">| {session.start_time}{session.end_time ? ` — ${session.end_time}` : ''} {tzShortLabel(session.timezone || user?.timezone)}</span>
                            )}
                            {!session.start_time && (
                              <span className="ml-1">at {new Date(session.created_at).toLocaleTimeString()}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      {session.notes && (
                        <p className="text-xs sm:text-sm text-gray-700 bg-gray-50 p-3 rounded-xl mb-3 break-words">{session.notes}</p>
                      )}
                      {session.duration_seconds > 0 && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {Math.floor(session.duration_seconds / 60)}m {session.duration_seconds % 60}s
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-end sm:items-start gap-1.5 sm:gap-2 shrink-0">
                      <div className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${getStatusColor(session.transcription_status)}`}>
                        {session.transcription_status}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => openEditSession(session, e)}
                          data-testid={`edit-session-${session.id}`}
                          title="Edit session"
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-indigo-600 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => openDeleteSession(session, e)}
                          data-testid={`delete-session-${session.id}`}
                          title="Delete session"
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ─── Edit Session Dialog ─────────────────────────────── */}
      <Dialog open={!!editingSession} onOpenChange={(open) => !open && setEditingSession(null)}>
        <DialogContent data-testid="edit-session-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="edit-session-type">Session Type</Label>
              <select
                id="edit-session-type"
                data-testid="edit-session-type-select"
                value={editDraft.session_type}
                onChange={(e) => setEditDraft(d => ({ ...d, session_type: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="initial">Initial</option>
                <option value="follow-up">Follow-up</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-session-date">Date ({tzShortLabel(editingSession?.timezone || user?.timezone)})</Label>
                <input
                  id="edit-session-date"
                  type="date"
                  data-testid="edit-session-date-input"
                  value={editDraft.session_date}
                  onChange={(e) => setEditDraft(d => ({ ...d, session_date: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 text-sm"
                />
              </div>
              <div></div>
              <div>
                <Label htmlFor="edit-start-time">Start time</Label>
                <input
                  id="edit-start-time"
                  data-testid="edit-start-time-input"
                  placeholder="e.g. 10:30 AM"
                  value={editDraft.start_time}
                  onChange={(e) => setEditDraft(d => ({ ...d, start_time: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="edit-end-time">End time</Label>
                <input
                  id="edit-end-time"
                  data-testid="edit-end-time-input"
                  placeholder="e.g. 11:00 AM"
                  value={editDraft.end_time}
                  onChange={(e) => setEditDraft(d => ({ ...d, end_time: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 text-sm"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-session-notes">Session Notes</Label>
              <Textarea
                id="edit-session-notes"
                data-testid="edit-session-notes"
                rows={4}
                value={editDraft.notes}
                onChange={(e) => setEditDraft(d => ({ ...d, notes: e.target.value }))}
                placeholder="Any additional notes about this session…"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditingSession(null)}
                data-testid="edit-session-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={saveSessionEdit}
                disabled={savingEdit}
                data-testid="edit-session-save"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Delete Session Confirmation Dialog ──────────────── */}
      <Dialog open={!!deletingSession} onOpenChange={(open) => !open && setDeletingSession(null)}>
        <DialogContent data-testid="delete-session-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete session?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              This permanently deletes the session, its transcript, the AI diagnosis,
              the medical SOAP note, the psychotherapy note, and any EHR push history.
            </p>
            <p className="font-semibold text-red-700">
              This action cannot be undone.
            </p>
            {deletingSession && (
              <div className="bg-gray-50 p-3 rounded-lg text-xs">
                <div><b>Type:</b> {deletingSession.session_type}</div>
                <div><b>Date:</b> {deletingSession.session_date || new Date(deletingSession.created_at).toLocaleDateString()}</div>
                {deletingSession.notes && <div className="mt-1"><b>Notes:</b> {deletingSession.notes}</div>}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeletingSession(null)}
                data-testid="delete-session-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDeleteSession}
                disabled={deleting}
                data-testid="delete-session-confirm"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete permanently'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ──────── Edit patient modal ──────── */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="edit-patient-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Pencil className="w-5 h-5" /> Edit patient
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">First name</label>
                <input
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  data-testid="edit-first-name"
                  className="mt-1 w-full rounded-lg text-sm p-2.5 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Last name</label>
                <input
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  data-testid="edit-last-name"
                  className="mt-1 w-full rounded-lg text-sm p-2.5 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">Date of birth</label>
              <input
                type="date"
                value={editForm.date_of_birth}
                onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })}
                data-testid="edit-dob"
                className="mt-1 w-full rounded-lg text-sm p-2.5 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-gray-600">MRN (optional)</label>
              <input
                type="text"
                value={editForm.mrn}
                onChange={(e) => setEditForm({ ...editForm, mrn: e.target.value })}
                data-testid="edit-mrn"
                className="mt-1 w-full rounded-lg text-sm p-2.5 border border-gray-300 font-mono focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={editForm.consent_granted}
                onChange={(e) => setEditForm({ ...editForm, consent_granted: e.target.checked })}
                data-testid="edit-consent"
                className="w-4 h-4 rounded"
              />
              Consent granted
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setEditModalOpen(false)} disabled={editSaving} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={saveEditedPatient}
              disabled={editSaving}
              data-testid="edit-patient-save-btn"
              className="rounded-xl bg-gradient-to-r from-purple-600 to-slate-700 text-white hover:from-purple-700 hover:to-slate-800"
            >
              {editSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ──────── Delete patient confirmation modal ──────── */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="delete-patient-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Delete {patient?.first_name} {patient?.last_name}?
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <p className="text-sm text-gray-700 leading-relaxed">
              The patient will be moved to <strong>Trash</strong> and hidden from your active list. You have <strong>24 hours</strong> to restore them from your user menu → Trash. After that window, the patient and <strong>every session, transcript, SOAP note, psychotherapy note, diagnosis, and push history record</strong> tied to them are permanently erased.
            </p>
            <div className="rounded-lg bg-red-50 ring-1 ring-red-200 p-3 text-xs text-red-900 space-y-1">
              <p>• {sessions.length} session{sessions.length !== 1 ? 's' : ''} will be queued for deletion</p>
              <p>• Restorable from user menu → <strong>Trash</strong> within 24h</p>
              <p>• Audit trail entries are retained per HIPAA</p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={deletingPatient} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={deletePatientNow}
              disabled={deletingPatient}
              data-testid="delete-patient-confirm-btn"
              className="rounded-xl bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingPatient ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Move to Trash
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
